import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { StyledLinearProgress } from '../UI/LoadingComponent/style';
import PolicyIcon from '@mui/icons-material/Policy';
import _ from 'lodash';

export const useUploadFileLoading = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getErrorMessage = useCallback(
    (errorMessage: string, isUpdateFile = false) => {
      if (_.startsWith(errorMessage, 'Malware found')) {
        if (isUpdateFile) {
          return 'Malware found. Please try another file. The previous uploaded file is still valid to use!';
        } else {
          return 'Malware found. Please try another file.';
        }
      }
      return errorMessage;
    },
    [],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, []);

  const handleCloseUploadFileLoading = useCallback(
    (_, reason?: string) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        return;
      }

      handleClose();
    },
    [handleClose],
  );

  const startUploadFileLoading = useCallback(() => {
    setIsLoading(true);
    setIsOpen(true);
  }, []);

  const renderUploadFileLoading = useCallback(() => {
    if (!isOpen) return null;

    const boxStyles = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: 3,
      gap: 2,
    };

    const iconStyles = {
      fontSize: 64,
    };

    const renderUploading = () => (
      <Box sx={boxStyles}>
        <PolicyIcon
          sx={{
            ...iconStyles,
            color: 'primary.200',
          }}
        />
        <Typography variant="body1">
          {`Please wait while we upload and scan your file.`}
        </Typography>
        <StyledLinearProgress sx={{ width: '100%', marginTop: 2 }} />
      </Box>
    );

    return (
      <Dialog
        open={isOpen}
        onClose={handleCloseUploadFileLoading}
        aria-labelledby="upload-file-dialog-title"
        aria-describedby="upload-file-dialog-description"
        disableEscapeKeyDown
      >
        <DialogContent
          sx={{
            width: 500,
            padding: 0,
          }}
        >
          {isLoading && renderUploading()}
        </DialogContent>
      </Dialog>
    );
  }, [handleCloseUploadFileLoading, isLoading, isOpen]);

  return useMemo(
    () => ({
      startUpload: startUploadFileLoading,
      closeLoading: handleClose,
      getErrorMessage,
      renderLoading: renderUploadFileLoading,
    }),
    [
      startUploadFileLoading,
      renderUploadFileLoading,
      getErrorMessage,
      handleClose,
    ],
  );
};
